import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { compose } from 'redux';
import {
  makeSelectFeaturedProducts,
  getSelectedFeaturedProduct,
} from 'selectors/productsSelectors';
import { selectPageTrackingFullData } from 'selectors/trackingSelector';
import Carousel from 'shared-components/Carousel/Carousel';
import { VIEW_SIZE } from 'utils/device';
import { track, segmentEnum, btnLocationEnum } from 'utils/segment';
import ProductAccordion from 'Scenes/Products/ProductAccordion/ProductAccordion';
import withSelectedProductLink from 'HOC/withSelectedProductLink/withSelectedProductLink';
import withSelectedCategoryId from 'HOC/withSelectedCategoryId/withSelectedCategoryId';
import FeaturedProductCards from './FeaturedProductCards/FeaturedProductCards';
import FeaturedProductText from './FeaturedProductsText/FeaturedProductsText';
import styles from './FeaturedProducts.module.css';

const getCarouselSlideCount = (viewSize) => {
  switch (viewSize) {
    case VIEW_SIZE.small: {
      return 2;
    }
    case VIEW_SIZE.medium: {
      return 3;
    }
    case VIEW_SIZE.large: {
      return 4;
    }
    case VIEW_SIZE.xLarge: {
      return 4;
    }
    default: {
      return null;
    }
  }
};

const PRODUCT_CARD_MARGIN = 24;

const FeaturedProducts = ({
  viewSize,
  featuredProducts,
  selectedProduct,
  trackingData,
}) => {
  const [sliderWidth, setSliderWidth] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const history = useHistory();
  const totalProductCount = _.size(featuredProducts);

  if (!featuredProducts.length) return null;

  const carouselSlideCount = getCarouselSlideCount(viewSize);
  const productCardDiffPerSlide = carouselSlideCount - 1;
  const displayedProductCardMax = (currentSlide + 1) * carouselSlideCount;
  const slideTotal = Math.ceil(totalProductCount / carouselSlideCount);
  const isLastProduct =
    displayedProductCardMax - productCardDiffPerSlide === totalProductCount;
  const productCountDisplay = isLastProduct
    ? totalProductCount
    : `${displayedProductCardMax - productCardDiffPerSlide} - ${Math.min(
        totalProductCount,
        displayedProductCardMax
      )}`;

  //subtract PRODUCT_CARD_MARGIN to account for margin between cards
  const productCardWidth =
    (sliderWidth - PRODUCT_CARD_MARGIN * productCardDiffPerSlide) /
    carouselSlideCount;

  const handleClickControls = () => {
    track(segmentEnum.carouselSwiped, {
      ...trackingData,
      experience: 'Featured Carousel',
    });
    const params = new URLSearchParams(window.location.search);
    params.delete('product_link');
    history.replace({
      search: params.toString(),
    });
  };

  return (
    <div className={styles.container}>
      <FeaturedProductText
        productCountDisplay={productCountDisplay}
        totalProductCount={totalProductCount}
        displayProductCount={totalProductCount > carouselSlideCount}
      />
      {selectedProduct && (
        <ProductAccordion
          {...selectedProduct}
          className={styles.featuredProductsAccordion}
          positionLocation={btnLocationEnum.featuredCarousel}
        />
      )}
      <Carousel
        setParentSliderWidth={setSliderWidth}
        slideCount={slideTotal}
        receiveCurrentSlide={setCurrentSlide}
        carouselHeightClassName={styles.carouselHeight}
        handleClickControls={handleClickControls}
        cardMargin={PRODUCT_CARD_MARGIN}
      >
        <FeaturedProductCards
          productCardWidth={productCardWidth}
          viewSize={viewSize}
          slideCount={carouselSlideCount}
          products={featuredProducts}
          carouselHeightClassName={styles.carouselHeight}
          productCardMargin={PRODUCT_CARD_MARGIN}
          slideTotal={slideTotal}
        />
      </Carousel>
    </div>
  );
};

const featuredProductsSelector = makeSelectFeaturedProducts();

const mapStateToProps = (state, props) => {
  return {
    featuredProducts: featuredProductsSelector(state, props),
    selectedProduct: getSelectedFeaturedProduct(state, props),
    trackingData: selectPageTrackingFullData(state),
  };
};

FeaturedProducts.propTypes = {
  viewSize: PropTypes.string.isRequired,
  featuredProducts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedProduct: PropTypes.shape({}),
  trackingData: PropTypes.shape({}).isRequired,
};

FeaturedProducts.defaultProps = {
  selectedProduct: null,
};

export default compose(
  withSelectedProductLink,
  withSelectedCategoryId,
  connect(mapStateToProps)
)(FeaturedProducts);
